import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from '../config/seo';

/**
 * Shared per-page SEO tags: title, description, canonical, Open Graph,
 * Twitter card, and optional JSON-LD structured data.
 *
 * @param {string} title - Full document title.
 * @param {string} description - Meta description.
 * @param {string} path - Absolute path of the page (e.g. '/pricing'); combined with SITE_URL for the canonical URL.
 * @param {string} [ogType] - Open Graph type ('website' or 'article').
 * @param {string} [ogImage] - Absolute URL of the Open Graph image.
 * @param {object|object[]} [jsonLd] - One or more schema.org objects emitted as application/ld+json.
 */
const Seo = ({ title, description, path, ogType = 'website', ogImage = DEFAULT_OG_IMAGE, jsonLd }) => {
  const url = `${SITE_URL}${path}`;
  const jsonLdObjects = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={ogImage} />
      {jsonLdObjects.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
};

export default Seo;
